import { useEffect, useMemo, useState } from "react";
import { Button, Modal, message } from "antd";
import { useRecoilState } from "recoil";
import { brandKitsState } from "../../../../../state/atoms";
import BrandKitMultiSelect from "./BrandKitMultiSelect";
import BrandTemplateApi, { BrandTemplateDto } from "../../../../../api/BrandTemplateApi";
import "./CopyToBrandKitModal.scss";

interface CopyToBrandKitModalProps {
  open: boolean;
  templates: BrandTemplateDto[];
  currentBrandKitId?: string;
  onClose: () => void;
  onCopied?: (kitNames: string[]) => void;
}

export default function CopyToBrandKitModal({
  open,
  templates,
  currentBrandKitId,
  onClose,
  onCopied,
}: CopyToBrandKitModalProps) {
  const [brandKits] = useRecoilState(brandKitsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected([]);
    }
  }, [open]);

  const otherKits = useMemo(
    () => brandKits.filter((b) => b.id && b.id !== currentBrandKitId),
    [brandKits, currentBrandKitId]
  );

  const selectedNames = otherKits
    .filter((b) => selected.includes(b.id!))
    .map((b) => b.name || "Untitled");

  const copyableCount = templates.filter((t) => t.id).length;

  const confirmText =
    selected.length === 1
      ? `Copy to ${selectedNames[0]}`
      : selected.length > 1
      ? `Copy to ${selected.length} Brand Kits`
      : "Copy";

  function close() {
    if (copying) return;
    onClose();
  }

  function handleCopy() {
    const copyableIds = templates.filter((t) => t.id).map((t) => t.id!);
    if (selected.length === 0 || copyableIds.length === 0) return;
    setCopying(true);

    const targets = otherKits.filter((b) => selected.includes(b.id!));
    const targetIds = targets.map((b) => b.id!);

    BrandTemplateApi.copyBulk(copyableIds, targetIds)
      .then((resp) => {
        if (!resp.ok) throw new Error();
        const names = targets.map((b) => b.name || "Untitled");
        const tplNoun = `template${copyableIds.length === 1 ? "" : "s"}`;
        const kitNoun = `Brand Kit${names.length === 1 ? "" : "s"}`;
        message.success(`${copyableIds.length} ${tplNoun} copied to ${names.length} ${kitNoun}`);
        onCopied?.(names);
        onClose();
      })
      .catch(() => message.error("Failed to copy to Brand Kit"))
      .finally(() => setCopying(false));
  }

  return (
    <Modal
      className="copy-to-brand-kit-modal"
      open={open}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnClose
    >
      <header>
        <h2>Copy to other Brand Kit</h2>
        <p>Copies are independent — changes won't affect the originals.</p>
        {copyableCount > 1 && (
          <span className="copy-to-brand-kit-count">{copyableCount} templates selected</span>
        )}
      </header>

      <BrandKitMultiSelect kits={otherKits} selected={selected} onChange={setSelected} />

      <footer>
        <Button size="large" onClick={close} disabled={copying}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={handleCopy}
          loading={copying}
          disabled={selected.length === 0}
        >
          {confirmText}
        </Button>
      </footer>
    </Modal>
  );
}
